import type { CSSProperties } from "react";
import { styled } from "@pigment-css/react";
import type { Sentiment } from "@acto/genui-components-schema";

type TrendSize = "default" | "label";

const TrendValue = styled("p")<{ sentiment: Sentiment; size: TrendSize }>(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflowWrap: "anywhere",
  variants: [
    {
      props: { sentiment: "positive" },
      style: { color: theme.fieldSalesTwo.colors.status.success.main },
    },
    {
      props: { sentiment: "negative" },
      style: { color: theme.fieldSalesTwo.colors.status.error.main },
    },
    {
      props: { size: "label" },
      style: {
        fontSize: theme.fieldSalesTwo.font.size["14"],
        letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
      },
    },
  ],
}));

export const trendSentiment = (percent: number): Sentiment => {
  if (percent > 0) return "positive";
  if (percent < 0) return "negative";

  return "neutral";
};

const formatTrendPercent = (value: number): string => {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value}%`;
};

export const hasTrend = (percent: number | null): percent is number => percent !== null && percent !== 0;

/** Signed percent delta such as "+15%" or "-10%". Renders nothing when {@link hasTrend} is false. */
export function TrendPercent({
  percent,
  size = "default",
  style,
}: {
  percent: number | null;
  size?: TrendSize;
  style?: CSSProperties;
}) {
  if (!hasTrend(percent)) return null;

  return (
    <TrendValue sentiment={trendSentiment(percent)} size={size} style={style}>
      {formatTrendPercent(percent)}
    </TrendValue>
  );
}
